import { formatDateTime } from "@/lib/format";
import type { Geschaeftspartner, GeschaeftspartnerTyp } from "@/lib/kontakte";

export type GeschaeftspartnerColumnKey =
  | "kundennummer"
  | "kundencode"
  | "lieferantennummer"
  | "firmenname"
  | "email"
  | "telefon"
  | "strasse"
  | "plz"
  | "ort"
  | "ap_count"
  | "artikel_count"
  | "projekte_count"
  | "bestellungen_count"
  | "eingangsrechnungen_count"
  | "id"
  | "hero_contact_id"
  | "hero_synced_at"
  | "created_at"
  | "updated_at";

export const GESCHAEFTSPARTNER_REQUIRED_COLUMNS = ["firmenname"] as const;

const ALL_COLUMNS: GeschaeftspartnerColumnKey[] = [
  "kundennummer",
  "kundencode",
  "lieferantennummer",
  "firmenname",
  "email",
  "telefon",
  "strasse",
  "plz",
  "ort",
  "ap_count",
  "artikel_count",
  "projekte_count",
  "bestellungen_count",
  "eingangsrechnungen_count",
  "id",
  "hero_contact_id",
  "hero_synced_at",
  "created_at",
  "updated_at",
];

/** Nur für Kunden sinnvoll. */
const KUNDE_ONLY_COLUMNS: GeschaeftspartnerColumnKey[] = [
  "kundennummer",
  "kundencode",
  "projekte_count",
];
/** Nur für Lieferanten sinnvoll (Lieferantennummer, verknüpfte Artikel, Einkauf). */
const LIEFERANT_ONLY_COLUMNS: GeschaeftspartnerColumnKey[] = [
  "lieferantennummer",
  "artikel_count",
  "bestellungen_count",
  "eingangsrechnungen_count",
];

const DEFAULT_COLUMNS_KUNDE: GeschaeftspartnerColumnKey[] = [
  "kundennummer",
  "firmenname",
  "email",
  "telefon",
  "ort",
  "ap_count",
];

const DEFAULT_COLUMNS_LIEFERANT: GeschaeftspartnerColumnKey[] = [
  "lieferantennummer",
  "firmenname",
  "email",
  "telefon",
  "ort",
  "ap_count",
];

const COLUMN_LABELS: Record<GeschaeftspartnerColumnKey, string> = {
  kundennummer: "Kundennummer",
  kundencode: "Code",
  lieferantennummer: "Lieferantennummer",
  firmenname: "Name",
  email: "E-Mail",
  telefon: "Telefon",
  strasse: "Straße",
  plz: "PLZ",
  ort: "Ort",
  ap_count: "Ansprechpartner",
  artikel_count: "Artikel",
  projekte_count: "Projekte",
  bestellungen_count: "Bestellungen",
  eingangsrechnungen_count: "E-Rechnungen",
  id: "ID",
  hero_contact_id: "Hero-Kontakt-ID",
  hero_synced_at: "Hero-Sync",
  created_at: "Erstellt",
  updated_at: "Aktualisiert",
};

const DEFAULT_WIDTHS: Partial<Record<GeschaeftspartnerColumnKey, number>> = {
  kundennummer: 120,
  kundencode: 90,
  lieferantennummer: 140,
  firmenname: 280,
  email: 220,
  telefon: 150,
  strasse: 200,
  plz: 80,
  ort: 160,
  ap_count: 140,
  artikel_count: 100,
  projekte_count: 110,
  bestellungen_count: 130,
  eingangsrechnungen_count: 130,
  id: 72,
  hero_contact_id: 130,
  hero_synced_at: 170,
  created_at: 170,
  updated_at: 170,
};

/** Server-seitig sortierbare Spalten (siehe api/kontakte.py `_GESCHAEFTSPARTNER_SORT_SQL`). */
export const GESCHAEFTSPARTNER_SORTABLE_COLUMNS = new Set<GeschaeftspartnerColumnKey>([
  "id",
  "kundennummer",
  "kundencode",
  "lieferantennummer",
  "firmenname",
  "email",
  "telefon",
  "strasse",
  "plz",
  "ort",
  "created_at",
  "updated_at",
]);

export const GESCHAEFTSPARTNER_COL_MIN_WIDTH = 56;
export const GESCHAEFTSPARTNER_COL_MAX_WIDTH = 560;

export function getAvailableGeschaeftspartnerColumns(
  typ: GeschaeftspartnerTyp,
): GeschaeftspartnerColumnKey[] {
  const excluded = typ === "kunde" ? LIEFERANT_ONLY_COLUMNS : KUNDE_ONLY_COLUMNS;
  return ALL_COLUMNS.filter((col) => !excluded.includes(col));
}

export function getDefaultGeschaeftspartnerColumns(
  typ: GeschaeftspartnerTyp,
): GeschaeftspartnerColumnKey[] {
  return typ === "kunde" ? [...DEFAULT_COLUMNS_KUNDE] : [...DEFAULT_COLUMNS_LIEFERANT];
}

export function normalizeGeschaeftspartnerColumnSelection(
  selected: string[] | null | undefined,
  typ: GeschaeftspartnerTyp,
): GeschaeftspartnerColumnKey[] {
  const available = getAvailableGeschaeftspartnerColumns(typ);
  const allowed = new Set(available);
  const out: GeschaeftspartnerColumnKey[] = [];
  for (const col of selected || []) {
    const key = col as GeschaeftspartnerColumnKey;
    if (allowed.has(key) && !out.includes(key)) out.push(key);
  }
  for (const required of GESCHAEFTSPARTNER_REQUIRED_COLUMNS) {
    if (!out.includes(required)) out.unshift(required);
  }
  if (out.length === 0) return getDefaultGeschaeftspartnerColumns(typ);
  return out;
}

export function getGeschaeftspartnerColumnLabel(key: string): string {
  return COLUMN_LABELS[key as GeschaeftspartnerColumnKey] ?? key;
}

export function getGeschaeftspartnerColumnDefaultWidth(key: string): number {
  return DEFAULT_WIDTHS[key as GeschaeftspartnerColumnKey] ?? 140;
}

export function clampGeschaeftspartnerColumnWidth(width: number): number {
  return Math.min(
    GESCHAEFTSPARTNER_COL_MAX_WIDTH,
    Math.max(GESCHAEFTSPARTNER_COL_MIN_WIDTH, Math.round(width)),
  );
}

export function formatOrt(row: Geschaeftspartner): string {
  return [row.plz, row.ort].filter(Boolean).join(" ") || "—";
}

export function initialsFromName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (!first) return "?";
  if (!second) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ""}${second[0] ?? ""}`.toUpperCase();
}

function formatCount(value: number | null | undefined): string {
  return value != null ? value.toLocaleString("de-DE") : "—";
}

/**
 * Kurztext zur Aktivität für den „Top zuerst“-Modus:
 * Kunden → Projekte, Lieferanten → Bestellungen und erkannte Eingangsrechnungen.
 */
export function formatGeschaeftspartnerAktivitaet(
  row: Geschaeftspartner,
  typ: GeschaeftspartnerTyp,
): string {
  if (typ === "kunde") {
    const n = row.projekte_count ?? 0;
    return n === 1 ? "1 Projekt" : `${n.toLocaleString("de-DE")} Projekte`;
  }
  const bestellungen = row.bestellungen_count ?? 0;
  const rechnungen = row.eingangsrechnungen_count ?? 0;
  const parts: string[] = [];
  if (bestellungen > 0) {
    parts.push(bestellungen === 1 ? "1 Bestellung" : `${bestellungen.toLocaleString("de-DE")} Bestellungen`);
  }
  if (rechnungen > 0) {
    parts.push(rechnungen === 1 ? "1 E-Rechnung" : `${rechnungen.toLocaleString("de-DE")} E-Rechnungen`);
  }
  return parts.join(" · ") || "Keine Aktivität";
}

export interface GeschaeftspartnerCellValue {
  value: string;
  alignRight?: boolean;
  mono?: boolean;
}

export function renderGeschaeftspartnerCellValue(
  row: Geschaeftspartner,
  key: GeschaeftspartnerColumnKey,
): GeschaeftspartnerCellValue {
  switch (key) {
    case "kundennummer":
      return { value: row.kundennummer?.trim() || "—", alignRight: true, mono: true };
    case "kundencode":
      return { value: row.kundencode != null ? String(row.kundencode) : "—", alignRight: true, mono: true };
    case "lieferantennummer":
      return { value: row.lieferantennummer?.trim() || "—", alignRight: true, mono: true };
    case "firmenname":
      return { value: row.firmenname };
    case "email":
      return { value: row.email || "—" };
    case "telefon":
      return { value: row.telefon || "—" };
    case "strasse":
      return { value: row.strasse || "—" };
    case "plz":
      return { value: row.plz || "—", mono: true };
    case "ort":
      return { value: formatOrt(row) };
    case "ap_count":
      return { value: String(row.ansprechpartner_count ?? 0), alignRight: true, mono: true };
    case "artikel_count":
      return {
        value: row.artikel_count != null ? String(row.artikel_count) : "—",
        alignRight: true,
        mono: true,
      };
    case "projekte_count":
      return { value: formatCount(row.projekte_count), alignRight: true, mono: true };
    case "bestellungen_count":
      return { value: formatCount(row.bestellungen_count), alignRight: true, mono: true };
    case "eingangsrechnungen_count":
      return { value: formatCount(row.eingangsrechnungen_count), alignRight: true, mono: true };
    case "id":
      return { value: String(row.id), alignRight: true, mono: true };
    case "hero_contact_id":
      return {
        value: row.hero_contact_id != null ? String(row.hero_contact_id) : "—",
        alignRight: true,
        mono: true,
      };
    case "hero_synced_at":
      return { value: formatDateTime(row.hero_synced_at) };
    case "created_at":
      return { value: formatDateTime(row.created_at) };
    case "updated_at":
      return { value: formatDateTime(row.updated_at) };
    default:
      return { value: "—" };
  }
}
